import { useRef, useState } from 'react'
import { Upload, Loader2, Trash2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import { BannerCropModal } from './BannerCropModal'

interface UserImageUploadProps {
  type: 'profile' | 'banner'
  currentImageUrl?: string
  onUpload: (file: File) => Promise<void>
  onDelete?: () => Promise<void>
  isUploading: boolean
  isDeleting?: boolean
}

export function UserImageUpload({ 
  type, 
  currentImageUrl, 
  onUpload, 
  onDelete,
  isUploading,
  isDeleting = false
}: UserImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [showCropModal, setShowCropModal] = useState(false)
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB')
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      const imageSrc = reader.result as string
      setSelectedImageSrc(imageSrc)
      
      // For banner, show crop modal. For profile, upload directly
      if (type === 'banner') {
        setShowCropModal(true)
      } else {
        setPreview(imageSrc)
        onUpload(file)
      }
    }
    reader.readAsDataURL(file)
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropComplete = async (croppedFile: File) => {
    setPreview(URL.createObjectURL(croppedFile))
    await onUpload(croppedFile)
    setShowCropModal(false)
    setSelectedImageSrc(null)
  }

  const handleDelete = async () => {
    if (!onDelete) return
    if (!confirm(`Are you sure you want to delete your ${type} image?`)) return
    
    try {
      await onDelete()
      setPreview(null)
    } catch (error) {
      alert('Failed to delete image. Please try again.')
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const displayUrl = preview || currentImageUrl

  if (type === 'banner') {
    return (
      <>
        <div 
          className="relative w-full h-full group cursor-pointer"
          onClick={handleClick}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Banner preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-adventure-100">
              <div className="text-center text-adventure-700">
                <Upload className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">Click to upload banner</p>
              </div>
            </div>
          )}
          
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
            <div className="absolute top-4 left-4 flex gap-2">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                handleClick()
              }}
              disabled={isUploading || isDeleting}
              variant="secondary"
              size="lg"
              className="pointer-events-auto"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {displayUrl ? 'Change Banner' : 'Upload Banner'}
                </>
              )}
            </Button>
            {displayUrl && onDelete && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleDelete()
                }}
                disabled={isUploading || isDeleting}
                variant="destructive"
                size="lg"
                className="pointer-events-auto"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            )}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {showCropModal && selectedImageSrc && (
          <BannerCropModal
            isOpen={showCropModal}
            onClose={() => {
              setShowCropModal(false)
              setSelectedImageSrc(null)
            }}
            imageSrc={selectedImageSrc}
            onCropComplete={handleCropComplete}
          />
        )}
      </>
    )
  }

  // Profile image upload
  return (
    <div 
      className="relative group cursor-pointer"
      onClick={handleClick}
    >
      <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-200 relative z-0">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="Profile preview"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Upload className="w-8 h-8 text-gray-400" />
          </div>
        )}
      </div>

      <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-10 pointer-events-none">
        <Button
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            handleClick()
          }}
          disabled={isUploading || isDeleting}
          variant="secondary"
          size="sm"
          className="pointer-events-auto"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </Button>
        {displayUrl && onDelete && (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              handleDelete()
            }}
            disabled={isUploading || isDeleting}
            variant="destructive"
            size="sm"
            className="rounded-full pointer-events-auto"
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}

